import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Flex, Text, Button } from '@radix-ui/themes';

import {
  createAudience,
  updateAudience,
} from '../../../../lib/redux/actions.jsx';
import TextField from '../../../../shared/TextField/TextField.jsx';
import { ColorsPicker } from '../../../../shared/SettingsModal/ColorsPicker.jsx';
import RadixSelect from '../../../../shared/RadixSelect';
import {
  convertAudienceToFormState,
  convertFormStateToApiFormat,
} from './utils/convertAudienceData';
import { AudiencePreview } from './AudiencePreview';
import styles from './AudienceForm.module.css';

const RemoveGroupButton = ({ onClick }) => (
  <button type="button" onClick={onClick} className={styles.removeGroupButton}>
    <i className="fa-thin fa-xmark" />
  </button>
);

const AudienceForm = ({ item: audience, closeModal }) => {
  const { t } = useTranslation(['edit', 'shared']);
  const [segmentGroups, setSegmentGroups] = useState([{ id: 0, segments: [] }]);
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasInitialized = useRef(false);

  const hasSegments = segmentGroups.some(group => group.segments.length > 0);

  const label = t('edit:audienceForm:label');
  const dispatch = useDispatch();
  const rawPopulationSegments = useSelector(
    state => state.status.populationSegments,
  );
  const populationSegments = useMemo(
    () =>
      rawPopulationSegments.map(populationSegment => ({
        value: populationSegment.id,
        label: populationSegment.name,
        userCount:
          populationSegment.user_count ||
          populationSegment.user_population_segments_count ||
          0,
      })),
    [rawPopulationSegments],
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    // Only initialize once when we have both audience data and population segments
    if (audience && populationSegments.length > 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      setName(audience.label || audience.name);
      setColor(audience.color);
      setSegmentGroups(
        convertAudienceToFormState(audience, populationSegments),
      );
    }
  }, [audience, populationSegments]);

  const addSegmentGroup = () => {
    const newId = Math.max(...segmentGroups.map(g => g.id)) + 1;
    setSegmentGroups([...segmentGroups, { id: newId, segments: [] }]);
  };

  const removeSegmentGroup = groupId => {
    setSegmentGroups(segmentGroups.filter(g => g.id !== groupId));
  };

  const updateGroupSegments = (groupId, segments) => {
    setSegmentGroups(
      segmentGroups.map(g =>
        g.id === groupId ? { ...g, segments: segments || [] } : g,
      ),
    );
    // Clear segments error when user makes a selection
    if (errors.segments) {
      setErrors(prev => ({ ...prev, segments: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = t('edit:audienceForm.errors.nameRequired');
    }

    if (!color) {
      newErrors.color = t('edit:audienceForm.errors.colorRequired');
    }

    if (!hasSegments) {
      newErrors.segments = t('edit:audienceForm.errors.segmentsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNameChange = e => {
    setName(e.target.value);
    // Clear name error when user starts typing
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: null }));
    }
  };

  const handleColorChange = newColor => {
    setColor(newColor);
    // Clear color error when user selects a color
    if (errors.color) {
      setErrors(prev => ({ ...prev, color: null }));
    }
  };

  const parseApiErrors = async errorResponse => {
    try {
      const errorData = await errorResponse.json();
      if (errorData?.errors) {
        const newErrors = {};
        // Map API errors to form fields
        if (errorData.errors.name) {
          const nameErrors = errorData.errors.name;
          if (
            nameErrors.includes('has already been taken') ||
            nameErrors.some(e => e.includes('already been taken'))
          ) {
            newErrors.name = t('edit:audienceForm.errors.nameTaken');
          } else {
            newErrors.name = nameErrors.join(', ');
          }
        }
        if (errorData.errors.color) {
          newErrors.color = errorData.errors.color.join(', ');
        }
        if (Object.keys(newErrors).length > 0) {
          return newErrors;
        }
      }
    } catch {
      // If we can't parse the error, use generic message
    }
    return { general: t('edit:audienceForm.errors.saveFailed') };
  };

  const submitForm = async () => {
    // Validate before submitting
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    const audienceParams = {
      audience: {
        name: name.trim(),
        color,
        audience_population_segments_attributes: convertFormStateToApiFormat(
          segmentGroups,
        ),
      },
      id: audience?.value,
    };

    try {
      // Perform API call
      if (audience) {
        await dispatch(updateAudience(audienceParams));
      } else {
        await dispatch(createAudience(audienceParams));
      }
      // Only close modal on success
      closeModal();
      queryClient.refetchQueries('boardSettings');
      queryClient.refetchQueries('plans');
    } catch (error) {
      // Parse and display API errors
      const apiErrors = await parseApiErrors(error);
      setErrors(apiErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex direction="column" height="70vh" maxWidth="420px">
      <Box pr="2" className={styles.scrollableContent}>
        <Flex direction="column" gap="4">
          {errors.general && (
            <Text size="2" color="red" weight="medium">
              {errors.general}
            </Text>
          )}

          <TextField
            name="audienceName"
            label={label}
            placeholder={label}
            onChange={handleNameChange}
            value={name}
            error={errors.name}
          />

          <Box>
            <Text
              as="label"
              size="2"
              mb="2"
              color={errors.segments ? 'red' : undefined}
            >
              Select Population Segment(s)
            </Text>

            {segmentGroups.map((group, index) => {
              // Filter out segments that are already selected in OTHER groups
              const selectedInOtherGroups = segmentGroups
                .filter(g => g.id !== group.id)
                .flatMap(g => g.segments.map(s => s.value));

              const availableOptions = populationSegments.filter(
                option => !selectedInOtherGroups.includes(option.value),
              );

              return (
                <Box key={group.id} mb="3">
                  {index > 0 && (
                    <Flex justify="center" my="3">
                      <Text weight="bold" size="2" color="gray">
                        AND
                      </Text>
                    </Flex>
                  )}
                  <Box
                    className={`${styles.segmentGroupContainer} ${
                      errors.segments ? styles.segmentGroupError : ''
                    }`}
                  >
                    {index > 0 && (
                      <RemoveGroupButton
                        onClick={() => removeSegmentGroup(group.id)}
                      />
                    )}
                    <RadixSelect
                      onChange={value => updateGroupSegments(group.id, value)}
                      placeholder="Select segments..."
                      value={group.segments}
                      isClearable={true}
                      options={availableOptions}
                      isSearchable={true}
                      isMulti={true}
                    />
                  </Box>
                </Box>
              );
            })}
            {errors.segments && (
              <Text size="1" color="red" mt="1">
                {errors.segments}
              </Text>
            )}

            <Flex justify="center" mt="3">
              <Button
                type="button"
                variant="outline"
                onClick={addSegmentGroup}
                style={{ gap: '8px' }}
              >
                <i className="fa-solid fa-plus" />
                AND
              </Button>
            </Flex>
          </Box>

          <Box>
            <ColorsPicker
              color={color}
              resourceType="audience"
              setColor={handleColorChange}
              error={errors.color}
            />
          </Box>

          {hasSegments && <AudiencePreview segmentGroups={segmentGroups} />}
        </Flex>
      </Box>

      <Flex gap="3" justify="end" className={styles.footer}>
        <Button
          size="3"
          type="button"
          variant="soft"
          color="gray"
          onClick={() => closeModal()}
        >
          {t('shared:cancel')}
        </Button>
        <Button
          size="3"
          type="button"
          disabled={isSubmitting}
          onClick={submitForm}
        >
          {t('shared:save')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default AudienceForm;
